import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { CookieConsentService } from '../../shared/cookie-consent.service';

@Component({
  selector: 'app-cookie-consent',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './cookie-consent.html',
  styleUrl: './cookie-consent.scss',
})
export class CookieConsent {
  private readonly consent = inject(CookieConsentService);
  private readonly platformId = inject(PLATFORM_ID);

  /**
   * Gate the prompt until the client has finished the first render.
   * Avoids Safari dead-clicks from SSR markup + withEventReplay mismatches.
   */
  private readonly clientReady = signal(false);
  /** Local dismiss latch so the prompt closes even if change detection is delayed. */
  private readonly dismissed = signal(false);
  readonly showPrompt = computed(
    () =>
      this.clientReady() &&
      this.consent.needsPrompt() &&
      !this.dismissed()
  );
  readonly errorMessage = signal('');
  isSubmitting = false;

  constructor() {
    afterNextRender(() => {
      if (isPlatformBrowser(this.platformId)) {
        this.clientReady.set(true);
      }
    });
  }

  accept(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isSubmitting || this.dismissed() || !this.consent.needsPrompt()) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessage.set('');
    this.dismissed.set(true);

    this.consent.accept().subscribe({
      next: () => {
        this.isSubmitting = false;
      },
      error: () => {
        this.isSubmitting = false;
      },
    });
  }

  decline(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isSubmitting || this.dismissed() || !this.consent.needsPrompt()) {
      return;
    }

    this.errorMessage.set('');
    this.dismissed.set(true);
    this.consent.decline();
  }
}
